/**
 * StrategyModal Component
 *
 * Modal for creating new trading strategies.
 * Form with name (required), description (optional), and philosophy (optional).
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TextArea,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

/**
 * Props for StrategyModal
 */
export interface StrategyModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful save - receives new strategy ID */
  onSave: (newStrategyId?: string) => void;
}

/**
 * Form data for strategy modal
 */
interface StrategyFormData {
  name: string;
  description: string;
  philosophy: string;
}

/**
 * Initial empty form state
 */
const initialFormData: StrategyFormData = {
  name: '',
  description: '',
  philosophy: '',
};

/**
 * StrategyModal Component
 */
export const StrategyModal: React.FC<StrategyModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  // Form state
  const [formData, setFormData] = useState<StrategyFormData>(initialFormData);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field: keyof StrategyFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (field === 'name' && nameError) {
      setNameError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedName = formData.name.trim();
      const trimmedDescription = formData.description.trim();
      const trimmedPhilosophy = formData.philosophy.trim();

      // Build create payload
      const createData: Parameters<typeof strategyService.add>[0] = {
        name: trimmedName,
        playbookIds: [],
      };

      // Only include description if non-empty
      if (trimmedDescription) {
        createData.description = trimmedDescription;
      }

      // Only include philosophy if non-empty
      if (trimmedPhilosophy) {
        createData.philosophy = trimmedPhilosophy;
      }

      const newStrategy = await strategyService.add(createData);
      syncCoordinatorService.notifyChange('strategies', newStrategy.id);

      showToast({
        title: 'Strategy Created',
        description: `"${trimmedName}" has been created`,
        color: 'success',
      });

      onSave(newStrategy.id);
      onClose();
    } catch (error) {
      console.error('Failed to create strategy:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create strategy';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
    >
      <ModalContent>
        <ModalHeader>New Strategy</ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter strategy name"
            value={formData.name}
            onValueChange={(value) => handleFieldChange('name', value)}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Description */}
          <TextArea
            label="Description"
            placeholder="Brief description of this strategy (optional)"
            value={formData.description}
            onValueChange={(value) => handleFieldChange('description', value)}
            minRows={2}
            maxRows={4}
            className="[&_textarea]:text-base"
          />

          {/* Philosophy */}
          <TextArea
            label="Philosophy"
            placeholder="Describe the trading philosophy (optional)"
            value={formData.philosophy}
            onValueChange={(value) => handleFieldChange('philosophy', value)}
            minRows={3}
            maxRows={6}
            className="[&_textarea]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            Create Strategy
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StrategyModal;
